import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import _orderBy from "lodash/orderBy";
import { useParams } from "react-router-dom";
import ReactTable from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import Cookies from "js-cookie";
import SectionDescription from "components/lib/Shared/SectionDescription";
import { setShares } from "state/slices/tables/sharesAllocationTable";
import {
  setJointShareholders,
  setCurrentJSGroupID,
} from "state/slices/tables/jointShareholderTable";
import {
  deleteCompanyOfficialAsync,
  getCompanyOfficialsAsync,
  updateCompanyOfficialAsync,
} from "state/slices/tables/companyOfficialsSlice";
import {
  deleteBeneficialOwnerAsync,
  editBeneficialOwnerAsync,
  updateDropdownEntryStatusAsync,
} from "state/slices/tables/beneficialOwnersTableSlice";
import { useToast } from "hooks/useToast";
import {
  deleteAccessRequest,
  getAccessRequests,
  sendAccessRequestInvite,
} from "state/slices/accessRequest";
import { getFullName, getMaskedEmail } from "utils";
import NotificationBanner from "components/lib/Shared/NotificationBanner";
import SubRowAsync from "./SubRowAsync";
import Checkbox from "components/lib/Shared/Checkbox";
import { Constants } from "config/constants";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import SearchInput from "components/lib/Shared/SearchInput";
import ChangeOfParticularsSubRow from "components/CompanyDashboard/MaintainYourCompany/Changes/OfficialsShareholdersAuthorisedPersons/ChangeOfParticulars/ChangeOfParticularsSubRow";
import CessationSubRowForDelete from "components/CompanyDashboard/MaintainYourCompany/Changes/OfficialsShareholdersAuthorisedPersons/Cessation/CessationSubRow";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import {
  deleteInitiatedTransactionChangeAsync,
  resetTransactionChange,
} from "state/slices/initiateTransactionChange";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import ShareChangesSubRow from "./ShareChangesSubRow";
import ManualTransactionReviewContext from "context/ManualTransactionReviewContext";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import JointShareholderTableSubrow from "./JointShareholderTableSubrow";
import { getCompany } from "state/slices/company";
import ShareholderPercentageWarningIcon from "./ShareholderPercentageWarningIcon";
import {
  calculateShareholdingPercentagesCrossTable,
  compareShareholdingPercentages,
  getShareholdersFromOfficials,
  getAggregatedShareholdersFromOfficials,
} from "utils/shareholdingCalculations";

const CompanyOfficialsTable = (props) => {
  const {
    assigningRegistrar,
    registrarCandidates = [],
    isChangesList,
    changeType,
    data,
    setSelectedOfficial,
    setShowCompanyOfficialsForm,
    setMode,
    mode,
    setChangingShareholderDesignation,
  } = props;

  const currentTab = "stakeholderTab";
  const dispatch = useDispatch();
  const role = Cookies.get("role");
  const [selectedRegistrar, setSelectedRegistrar] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [isViewOnly, setIsViewOnly] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const { comments } = useSelector((state) => state.commentsSlice);
  const accessRequests = useSelector(
    (state) => state.accessRequestsSlice.items
  );
  const { company } = useSelector((state) => state.companySlice);
  const { beneficialOwners } = company;
  const currentUser = useSelector((state) => state.userSlice);
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const { items: initiatedTransactionChanges = [] } = useSelector(
    (state) => state.initiatedTransactionsSlice?.initiatedTransactions || {}
  );
  const { dashboardRoles } = useSelector(
    (state) => state.companyDashboardSlice
  );

  const { proRata, rightsIssue } = useSelector(
    (state) => state.shareAllotmentSlice || {}
  );

  const { toast } = useToast(5000);
  const params = useParams();
  const companyId = company?._id ?? params?.companyId;

  const {
    isInUpdateCompanyDetailsContext,
    selectionsMade,
    createdTransactionsIds,
    transactions,
    setCreatedTransactionsIds,
    setRefechTransactionsOnUpdate,
  } = useContext(UpdateCompanyDetailsContext);

  const manualTransactionReviewCtx = useContext(ManualTransactionReviewContext);
  const isInManualTransactionReviewContext =
    manualTransactionReviewCtx?.isInManualTransactionReviewContext ?? false;
  // subrow/rendering local state
  const [showShareChangesSubRow, setShowShareChangesSubRow] = useState(false);
  const [cessationIsSelected, setCessationIsSelected] = useState(false);
  const [changeOfParticularsIsSelected, setChangeOfParticularsIsSelected] =
    useState(false);
  const { changeOfParticularsInfo, setChangeOfParticularsInfo } =
    useContext(ManageModuleContext);
  const [editModeIsSelected, setEditModeIsSelected] = useState(false);
  const [jointShareholdersToChange, setJointShareholdersToChange] = useState(
    []
  );
  const originalCompanyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );

  const nominalShares =
    useSelector(
      (state) => state.companyDashboardSlice?.company?.nominalShares
    ) || [];
  const votesPerShareByClass = useMemo(() => {
    const obj = {};
    nominalShares.forEach((n) => {
      if (n?.sharesCategory) {
        obj[n.sharesCategory] = Number(n.votesPerShare || 0);
      }
    });
    return obj;
  }, [nominalShares]);

  // NEW: store what the user clicked so subrow gets the right data
  const [selectedProcessType, setSelectedProcessType] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [manualAllotmentData, setManualAllotmentData] = useState(null);
  const [boOnlyViewOfficialId, setBoOnlyViewOfficialId] = useState(null);
  const [initialSelectedTabByOfficial, setInitialSelectedTabByOfficial] =
    useState({});

  // State for tracking shareholding percentage changes across all officials
  // Note: This state is NOT persisted - warning icons will clear on page refresh
  const [shareholderPercentageChanges, setShareholderPercentageChanges] =
    useState({});

  useEffect(() => {
    try {
      const sourceTxs =
        isInUpdateCompanyDetailsContext && Array.isArray(transactions)
          ? transactions
          : initiatedTransactionChanges;
      const txs = Array.isArray(sourceTxs) ? sourceTxs : [];

      // Create mappings for Joint Shareholders
      const jsGroupMap = {};
      const groupLeaderMap = {};
      const groups = {};

      originalCompanyOfficials.forEach((official) => {
        if (official.JSGroupID) {
          if (!groups[official.JSGroupID]) groups[official.JSGroupID] = [];
          groups[official.JSGroupID].push(official);
        }
      });

      Object.keys(groups).forEach((groupId) => {
        const sorted = _orderBy(groups[groupId], ["JSGroupOrder"]);
        const leader = sorted[sorted.length - 1];
        if (leader) {
          groupLeaderMap[groupId] = String(leader._id);
          groups[groupId].forEach((member) => {
            jsGroupMap[String(member._id)] = groupId;
          });
        }
      });

      // Collect all share capital changes from relevant transactions
      const shareChanges = [];
      const allocatedIds = new Set();
      const votesPerShare = votesPerShareByClass || {};

      txs.forEach((tx) => {
        // Accept all transactions that have shareCapitalChanges
        if (!tx?.shareCapitalChanges || !Array.isArray(tx.shareCapitalChanges))
          return;

        tx.shareCapitalChanges.forEach((sc) => {
          const ct = String(sc?.changeType || "");
          // Allow all allotment-related changes
          if (
            ![
              "ALLOTMENT",
              "AUTHORIZATION-AND-ALLOTMENT",
              "privateWithMoreThanOneClass",
              "PVTwithOneClass",
              "existingAuth",
              "unallotedShares",
            ].includes(ct)
          )
            return;

          // Clone and map allocations to Group Leader if applicable
          const rawAllocs = Array.isArray(sc?.allocations)
            ? sc.allocations
            : [];
          const mappedAllocs = rawAllocs.map((a) => {
            const id = String(a?.officialId || "");
            let effectiveId = id;
            if (jsGroupMap[id]) {
              effectiveId = groupLeaderMap[jsGroupMap[id]] || id;
            }
            return { ...a, officialId: effectiveId };
          });

          const hasAllocations = mappedAllocs.some(
            (a) => Number(a?.allocatedShares || 0) > 0
          );

          // Check for allocations - if no allocations are made yet, we shouldn't trigger alerts
          // This prevents "Review" tags from appearing purely due to "Shares On Offer" dilution before actual allotment

          if (!hasAllocations) {
            return; // Skip if no allocations yet
          }

          shareChanges.push({ ...sc, allocations: mappedAllocs });

          mappedAllocs.forEach((a) => {
            const id = String(a?.officialId || "");
            const val = Number(a?.allocatedShares || 0);
            if (id && val > 0) allocatedIds.add(id);
          });
        });
      });

      if (shareChanges.length === 0 && !proRata && !rightsIssue) {
        // Only clear if we really want to reset (e.g. no active transactions)
        // If there are no shareChanges, there are no percentage changes to calculate.
        setShareholderPercentageChanges({});
        return;
      }

      // Add Pro Rata change if exists
      if (proRata && !shareChanges.some((sc) => sc.option === "proRata")) {
        const mappedProRataAllocs = (
          Array.isArray(proRata.allocations) ? proRata.allocations : []
        ).map((a) => {
          const id = String(a?.officialId || "");
          let effectiveId = id;
          if (jsGroupMap[id]) {
            effectiveId = groupLeaderMap[jsGroupMap[id]] || id;
          }
          return { ...a, officialId: effectiveId };
        });

        shareChanges.push({
          changeType: "ALLOTMENT",
          sharesOnOffer: proRata.sharesOnOffer,
          classOfShares: proRata.classOfShares,
          allocations: mappedProRataAllocs,
          option: "proRata",
        });

        mappedProRataAllocs.forEach((a) => {
          const id = String(a?.officialId || "");
          const val = Number(a?.allocatedShares || 0);
          if (id && val > 0) allocatedIds.add(id);
        });
      }

      // Add Rights Issue change if exists
      if (
        rightsIssue &&
        !shareChanges.some((sc) => sc.option === "rightsIssue")
      ) {
        const mappedRightsAllocs = (
          Array.isArray(rightsIssue.allocations) ? rightsIssue.allocations : []
        ).map((a) => {
          const id = String(a?.officialId || "");
          let effectiveId = id;
          if (jsGroupMap[id]) {
            effectiveId = groupLeaderMap[jsGroupMap[id]] || id;
          }
          return { ...a, officialId: effectiveId };
        });

        shareChanges.push({
          changeType: "ALLOTMENT",
          sharesOnOffer: rightsIssue.sharesOnOffer,
          classOfShares: rightsIssue.classOfShares,
          allocations: mappedRightsAllocs,
          option: "rightsIssue",
        });

        mappedRightsAllocs.forEach((a) => {
          const id = String(a?.officialId || "");
          const val = Number(a?.allocatedShares || 0);
          if (id && val > 0) allocatedIds.add(id);
        });
      }

      // Construct complete lists of shareholders using originalCompanyOfficials as baseline
      // This ensures we have the committed state before applying the pending changes
      // USE AGGREGATED SHAREHOLDERS to handle Joint Shareholder groups correctly
      const allOriginalShareholders = getAggregatedShareholdersFromOfficials(
        originalCompanyOfficials
      );

      // Split into Current vs Changes based on allocations (active transaction logic)
      const currentShareholders = allOriginalShareholders.filter(
        (s) => !allocatedIds.has(String(s._id || s.id))
      );
      const changesShareholders = allOriginalShareholders.filter((s) =>
        allocatedIds.has(String(s._id || s.id))
      );

      // 1. Original (Before) - Treat everyone as current for baseline
      const originalPercentages = calculateShareholdingPercentagesCrossTable(
        allOriginalShareholders,
        [],
        [],
        votesPerShare
      );

      // 2. New (After) - Apply share changes
      const newPercentages = calculateShareholdingPercentagesCrossTable(
        currentShareholders,
        changesShareholders,
        shareChanges,
        votesPerShare
      );

      // 3. Compare
      const changes = compareShareholdingPercentages(
        originalPercentages,
        newPercentages
      );

      if (Object.keys(changes).length > 0) {
        setShareholderPercentageChanges((prev) => ({ ...prev, ...changes }));
      }
    } catch (_) {}
  }, [
    originalCompanyOfficials,
    initiatedTransactionChanges,
    transactions,
    isInUpdateCompanyDetailsContext,
    votesPerShareByClass,
    proRata,
    rightsIssue,
  ]);

  /** Stakeholder selections */
  let stakeholderDetails = {};
  let changeOfParticularsDesignations = [];
  let cessationDesignations = [];
  let appointmentDesignations = [];

  /** Shares selections */
  let shareInformation = {};
  let capitalIncrease = [];
  let shareholdingChanges = [];

  if (isInUpdateCompanyDetailsContext && selectionsMade) {
    stakeholderDetails = selectionsMade?.stakeholderDetails;
    changeOfParticularsDesignations = stakeholderDetails?.changeOfParticulars;
    cessationDesignations = stakeholderDetails?.cessation;
    appointmentDesignations = stakeholderDetails?.appointment;

    shareInformation = selectionsMade?.shareInformation;
    capitalIncrease = shareInformation?.capitalIncrease;
    shareholdingChanges = shareInformation?.shareholdingChanges;
  }

  const canAppointDirectorShareholder = () => {
    if (appointmentDesignations && appointmentDesignations?.length > 0) {
      return appointmentDesignations.includes(
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
      );
    }
    return false;
  };

  const canCeaseDirectorShareholder = () => {
    if (cessationDesignations && cessationDesignations?.length > 0) {
      return cessationDesignations.includes(
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
      );
    }
    return false;
  };

  const isRegistrarReviewOrApprove = dashboardRoles.some(
    (r) => r.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );

  // Use refs to track if we've already triggered refetch
  const hasTriggeredRefetchRef = useRef(false);
  const lastTransactionsLengthRef = useRef(0);

  useEffect(() => {
    dispatch(
      getAccessRequests({
        query: {
          initiatedToEmail: currentUser.electronicAddress?.emailAddress,
        },
      })
    );
  }, [currentUser.electronicAddress?.emailAddress, dispatch]);

  // Ensure latest transactions are fetched ONCE when first entering changes list
  useEffect(() => {
    if (isChangesList && !hasTriggeredRefetchRef.current) {
      hasTriggeredRefetchRef.current = true;
      setRefechTransactionsOnUpdate(true);
    }

    // Reset when leaving changes list
    return () => {
      if (!isChangesList) {
        hasTriggeredRefetchRef.current = false;
      }
    };
  }, [isChangesList, setRefechTransactionsOnUpdate]);

  // Only update table when transactions count actually changes (not on every transaction reference change)
  useEffect(() => {
    if (isChangesList && transactions) {
      const currentLength = Array.isArray(transactions)
        ? transactions.length
        : 0;

      if (currentLength !== lastTransactionsLengthRef.current) {
        lastTransactionsLengthRef.current = currentLength;
        // Small delay to ensure data is fully propagated
        const timeoutId = setTimeout(() => {
          setCurrentPage((prev) => prev);
        }, 50);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [transactions, isChangesList]); // Only depends on transactions and isChangesList

  const transformSharesAllotted = (sharesAllotted) => {
    const grouped = sharesAllotted.reduce(
      (acc, { classOfShares, allottedShares }) => {
        acc[classOfShares] = (acc[classOfShares] || 0) + allottedShares;
        return acc;
      },
      {}
    );

    const html = Object.entries(grouped)
      .map(
        ([classOfShares, totalAllotted]) =>
          `${classOfShares}: ${totalAllotted} \n`
      )
      .join("");

    return html;
  };

  const getSharesFromTransactions = (officialId, shareCapitalChanges) => {
    if (!officialId || !Array.isArray(shareCapitalChanges)) {
      return "-";
    }

    const sharesByClass = new Map();

    shareCapitalChanges.forEach((transaction) => {
      // Handle both direct allocations array and nested structure
      const allocations = transaction.allocations || [];

      if (Array.isArray(allocations) && allocations.length > 0) {
        const officialAllocation = allocations.find(
          (allocation) => String(allocation.officialId) === String(officialId)
        );

        if (officialAllocation && transaction.classOfShares) {
          const allocatedShares =
            Number(officialAllocation.allocatedShares) || 0;
          const classOfShares = transaction.classOfShares;

          if (allocatedShares > 0) {
            if (sharesByClass.has(classOfShares)) {
              sharesByClass.set(
                classOfShares,
                sharesByClass.get(classOfShares)
              );
            } else {
              sharesByClass.set(classOfShares, allocatedShares);
            }
          }
        }
      }

      // Also check if the transaction itself has direct allocation data
      if (
        transaction.officialId &&
        String(transaction.officialId) === String(officialId)
      ) {
        const allocatedShares = Number(transaction.allocatedShares) || 0;
        const classOfShares = transaction.classOfShares;

        if (allocatedShares > 0 && classOfShares) {
          if (sharesByClass.has(classOfShares)) {
            sharesByClass.set(
              classOfShares,
              sharesByClass.get(classOfShares) + allocatedShares
            );
          } else {
            sharesByClass.set(classOfShares, allocatedShares);
          }
        }
      }
    });

    if (sharesByClass.size === 0) {
      return "-";
    }

    return Array.from(sharesByClass.entries())
      .map(([classOfShares, shares]) => `${classOfShares}: ${shares}`)
      .join(", ");
  };

  let onClick = (row) => {
    if (!row.isExpanded) {
      row.toggleAllRowsExpanded(false);
      row.toggleRowExpanded();
    } else {
      row.toggleRowExpanded();
    }
  };

  const handleClose = (row) => {
    setShowShareChangesSubRow(false);
    setChangeOfParticularsIsSelected(false);
    setCessationIsSelected(false);
    setEditModeIsSelected(false);
    setSelectedProcessType(null);
    setSelectedTransaction(null);
    setManualAllotmentData(null);
    setJointShareholdersToChange([]);
    onClick(row);
  };

  const handleView = (row) => {
    if (props?.isChangesList) {
      const JSGroupID = row.original?.JSGroupID;
      if (JSGroupID && transactions?.length > 0) {
        const jointShareholderTransaction = transactions.find(
          (trx) => trx.JSGroupID === JSGroupID
        );
        const jointShareholderForEdit = originalCompanyOfficials.filter(
          (official) => official.JSGroupID === JSGroupID
        );
        const jointShareholders = jointShareholderForEdit.filter((js) =>
          jointShareholderTransaction.individualOfficials.find(
            (off) => off._id === js._id
          )
        );
        const sortedJointShareholders = _orderBy(
          jointShareholders,
          "createdAt",
          "asc"
        );
        setJointShareholdersToChange(sortedJointShareholders);
      }
      setIsViewOnly(true);
      onClick(row);
    } else {
      dispatch(setShares(row?.sharesAllotted ?? []));
      props.handleView(row);
    }
  };

  const handleEdit = (row) => {
    if (props?.isChangesList) {
      setIsViewOnly(false);
      setEditModeIsSelected(true);
      onClick(row);
    } else {
      dispatch(setShares(row?.sharesAllotted ?? []));
      props.handleEdit(row);
    }
  };

  const handleEditAndDeleteJointShareholder = (row) => {
    const jointShareholderForEdit = originalCompanyOfficials.filter(
      (official) => official.JSGroupID === row.JSGroupID
    );
    dispatch(setCurrentJSGroupID(jointShareholderForEdit[0].JSGroupID));
    props.toggleShowOfficialsForm(true);
    const sortedJointShareholders = _orderBy(
      jointShareholderForEdit,
      "createdAt",
      "asc"
    );
    dispatch(setJointShareholders(sortedJointShareholders));
  };

  const handleDelete = async (row) => {
    const accessRequest = accessRequests.find(
      (request) => request.initiatedToEmail === row.initiatedToEmail
    );
    if (
      row?.type === "invited" &&
      row?.inviteStatus === "Awaiting Data" &&
      accessRequest?._id
    ) {
      dispatch(deleteAccessRequest({ accessRequestsId: accessRequest?._id }));
    }
    await Promise.all([
      dispatch(
        deleteCompanyOfficialAsync({
          officialId: row._id,
          idType: row.idType,
          companyId,
        })
      ),
      dispatch(
        updateDropdownEntryStatusAsync({
          beneficialOwnerId: row._id,
          data: { showInBoDropdown: false },
        })
      ),
      dispatch(
        deleteBeneficialOwnerAsync({
          beneficialOwnerId: row._id,
          companyId,
        })
      ),
    ]);
  };

  const handleReview = async (row) => {
    row?.getToggleRowExpandedProps()?.onClick();
  };

  const handleRecallAccess = async (row) => {
    const accessRequest = accessRequests.find(
      (request) => request.initiatedToEmail === row.initiatedToEmail
    );
    if (window.confirm("Are you sure you want to recall this request?")) {
      if (row?.type === "invited" && row?.inviteStatus === "Awaiting Data") {
        await dispatch(
          deleteAccessRequest({ accessRequestsId: accessRequest?._id })
        );
      }
      dispatch(
        deleteCompanyOfficialAsync({
          officialId: row._id,
          idType: row.idType,
          companyId,
        })
      );
      dispatch(
        updateDropdownEntryStatusAsync({
          beneficialOwnerId: row._id,
          data: { showInBoDropdown: false },
        })
      );
      dispatch(
        deleteBeneficialOwnerAsync({
          beneficialOwnerId: row._id,
          companyId,
        })
      );
    }
  };

  const handleResendInvite = async (row) => {
    try {
      await dispatch(
        sendAccessRequestInvite({
          data: { userEmail: row.initiatedToEmail },
        })
      );
      toast("success", "Invite sent successfully.");
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong please try again."
      );
    }
  };

  const handleToggleRegistrar = async (e) => {
    const idTypeOfSelectedRegistrar = registrarCandidates.find(
      (registrarCandidate) => registrarCandidate._id === e.target.value
    )?.idType;
    if (e.target.checked) {
      setSelectedRegistrar(e.target.value);
      await dispatch(
        updateCompanyOfficialAsync({
          officialId: e.target.value,
          data: {
            isRegistrar: true,
            idType: idTypeOfSelectedRegistrar,
            companyId,
          },
        })
      );
      toast("success", "Registrar assigned successfully.");
    } else {
      setSelectedRegistrar(null);
      await dispatch(
        updateCompanyOfficialAsync({
          officialId: e.target.value,
          data: {
            isRegistrar: false,
            idType: idTypeOfSelectedRegistrar,
            companyId,
          },
        })
      );
      toast("success", "Registrar removed successfully.");
    }
  };

  const registrarCandidatesIds = registrarCandidates.map(
    (registrarCandidate) => registrarCandidate._id
  );

  const processTypeToChangeType = {
    privateWithMoreThanOneClass: "ALLOTMENT",
    PVTwithOneClass: "ALLOTMENT",
    existingAuth: "ALLOTMENT",
    unallotedShares: "ALLOTMENT",
    shareTransfer: "SHARE-TRANSFER",
    transmissionOfShares: "TRANSMISSION-OF-SHARES",
    forfeitureOfShares: "FORFEITURE-OF-SHARES",
    surrenderOfShares: "SURRENDER-OF-SHARES",
    sharesBuyback: "SHARES-BUYBACK",
    cancelationOfTreasuryShares: "CANCELATION-OF-TREASURY-SHARES",
    saleTransferOfTreasuryShares: "SALE-TRANSFER-OF-TREASURY-SHARES",
    sharesRedemption: "SHARES-REDEMPTION",
    noticeToCourt: "NOTICE-TO-COURT",
    takeoverOffer: "TAKEOVER-OFFER",
    sharesConsolidation: "SHARES-CONSOLIDATION",
    sharesSubdivision: "SHARES-SUBDIVISION",
    noticeOfChangeOfName: "NOTICE-OF-CHANGE-OF-NAME",
    noticeOfParticulars: "NOTICE-OF-PARTICULARS",
    solvencyStatement: "SOLVENCY-STATEMENT",
    courtOrder: "COURT-ORDER",
    debenturesChargesRegistration: "DEBENTURES-CHARGES-REGISTRATION",
    debenturesChargesAmendment: "DEBENTURES-CHARGES-AMENDMENT",
  };

  const handleAppointAsDirector = (row) => {
    setMode({
      ...mode,
      editing: true,
    });
    setSelectedOfficial(row.original);
    setShowCompanyOfficialsForm(true);
    setChangingShareholderDesignation(true);
  };

  const handleCeaseAsDirector = (row) => {
    setMode({
      ...mode,
      editing: true,
    });
    setSelectedOfficial(row.original);
    setShowCompanyOfficialsForm(true);
    setChangingShareholderDesignation(true);
  };

  const handleChange = (row) => {
    const JSGroupID = row.original?.JSGroupID;
    if (JSGroupID) {
      const jointShareholderForEdit = originalCompanyOfficials.filter(
        (official) => official.JSGroupID === JSGroupID
      );
      const sortedJointShareholders = _orderBy(
        jointShareholderForEdit,
        "createdAt",
        "asc"
      );
      setJointShareholdersToChange(sortedJointShareholders);
    } else {
      setChangeOfParticularsInfo({
        ...changeOfParticularsInfo,
        selectedValueType: "changes-including-email",
      });
    }
    setChangeOfParticularsIsSelected(true);
    onClick(row);
  };

  // Normalize initiated transactions - use Redux state for real-time updates
  const normalizedTxns = transactions;
  // Build flags + detect manual allot availability
  const { flagsByProcess, manualAllotAvailable } = useMemo(() => {
    const flags = {
      privateWithMoreThanOneClass: false,
      PVTwithOneClass: false,
      existingAuth: false,
      unallotedShares: false,
      shareTransfer: false,
      transmissionOfShares: false,
      forfeitureOfShares: false,
      surrenderOfShares: false,
      sharesBuyback: false,
      cancelationOfTreasuryShares: false,
      saleTransferOfTreasuryShares: false,
      sharesRedemption: false,
      noticeToCourt: false,
      takeoverOffer: false,
      sharesConsolidation: false,
      sharesSubdivision: false,
    };

    let allotManual = false;

    for (const t of normalizedTxns) {
      if (t?.transactionType && t.transactionType !== "MANUAL") continue;
      const p = t?.processType;
      if (p && Object.prototype.hasOwnProperty.call(flags, p)) {
        flags[p] = true;
      }
      if (
        (p === "privateWithMoreThanOneClass" ||
          p === "PVTwithOneClass" ||
          p === "existingAuth" ||
          p === "unallotedShares") &&
        Array.isArray(t?.shareCapitalChanges)
      ) {
        const hasManual = t.shareCapitalChanges.some(
          (sc) =>
            (sc?.changeType === "privateWithMoreThanOneClass" ||
              sc?.changeType === "PVTwithOneClass" ||
              sc?.changeType === "existingAuth" ||
              sc?.changeType === "unallotedShares") &&
            String(sc?.option || "").toLowerCase() === "manual"
        );
        if (hasManual) allotManual = true;
      }
    }

    return { flagsByProcess: flags, manualAllotAvailable: allotManual };
  }, [normalizedTxns]);

  const hasAnyRemainderAcrossTxns = useMemo(() => {
    try {
      const allowedProcesses = [
        "privateWithMoreThanOneClass",
        "PVTwithOneClass",
        "existingAuth",
        "unallotedShares",
      ];
      const allowedOptions = [
        "manual",
        "proRata",
        "rightsIssue",
        "specificShareholderOffer",
        "generalOffer",
      ];
      for (const t of normalizedTxns) {
        if (t?.transactionType !== "MANUAL") continue;
        if (!allowedProcesses.includes(t?.processType)) continue;
        const scs = Array.isArray(t?.shareCapitalChanges)
          ? t.shareCapitalChanges
          : [];
        for (const sc of scs) {
          if (!allowedOptions.includes(String(sc?.option || ""))) continue;
          const remainder = Number(sc?.remainder || 0);
          if (remainder > 0) return true;
          const sharesOnOffer = Number(sc?.sharesOnOffer || 0);
          const allocated = Array.isArray(sc?.allocations)
            ? sc.allocations.reduce(
                (sum, a) => sum + Number(a?.allocatedShares || 0),
                0
              )
            : 0;
          if (sharesOnOffer > 0 && sharesOnOffer - allocated > 0) return true;
        }
      }
      return false;
    } catch (_) {
      return false;
    }
  }, [normalizedTxns]);

  const hasPendingManualAllocations = useMemo(() => {
    try {
      const allowedProcesses = [
        "privateWithMoreThanOneClass",
        "PVTwithOneClass",
        "existingAuth",
        "unallotedShares",
      ];
      for (const t of normalizedTxns) {
        if (t?.transactionType !== "MANUAL") continue;
        if (!allowedProcesses.includes(t?.processType)) continue;
        const scs = Array.isArray(t?.shareCapitalChanges)
          ? t.shareCapitalChanges
          : [];
        for (const sc of scs) {
          if (String(sc?.option || "").toLowerCase() !== "manual") continue;
          const remainder = Number(sc?.remainder || 0);
          if (remainder > 0) return true;
          const sharesOnOffer = Number(sc?.sharesOnOffer || 0);
          const allocated = Array.isArray(sc?.allocations)
            ? sc.allocations.reduce(
                (sum, a) => sum + Number(a?.allocatedShares || 0),
                0
              )
            : 0;
          if (sharesOnOffer > 0 && sharesOnOffer - allocated > 0) return true;
        }
      }
      return false;
    } catch (_) {
      return false;
    }
  }, [normalizedTxns]);

  const findTxnByProcess = (proc) =>
    normalizedTxns.find(
      (t) => t?.transactionType === "MANUAL" && t?.processType === proc
    );

  const openShareChanges = (row, proc) => {
    const txn = findTxnByProcess(proc) || null;
    setSelectedProcessType(proc);
    setSelectedTransaction(txn);
    setShowShareChangesSubRow(true);
    onClick(row);
  };

  // Handle percentage changes from ShareChangesSubRow and OfficialShareAllocation
  const handlePercentageChangesUpdate = (changes) => {
    // If changes is null or empty object, clear all warnings
    if (!changes || Object.keys(changes).length === 0) {
      setShareholderPercentageChanges(null);
      return;
    }

    // Merge with existing changes to avoid overwriting previous changes
    setShareholderPercentageChanges((prevChanges) => {
      const merged = { ...prevChanges, ...changes };

      return merged;
    });

    // Warning icons will clear on page refresh (not persisted)
  };

  // Function to manually clear warning icons (can be called when needed)
  const clearPercentageWarnings = () => {
    setShareholderPercentageChanges({});
  };

  const handleManualAllot = (row) => {
    const manualProc = [
      "privateWithMoreThanOneClass",
      "PVTwithOneClass",
      "existingAuth",
      "unallotedShares",
    ].find((p) => {
      const t = findTxnByProcess(p);
      return (
        t &&
        Array.isArray(t.shareCapitalChanges) &&
        t.shareCapitalChanges.some(
          (sc) =>
            sc?.changeType === p &&
            String(sc?.option || "").toLowerCase() === "manual"
        )
      );
    });

    const txn = manualProc ? findTxnByProcess(manualProc) : null;
    const manualSpec =
      txn?.shareCapitalChanges?.find(
        (sc) =>
          (sc?.changeType === "privateWithMoreThanOneClass" ||
            sc?.changeType === "PVTwithOneClass" ||
            sc?.changeType === "existingAuth" ||
            sc?.changeType === "unallotedShares") &&
          String(sc?.option || "").toLowerCase() === "manual"
      ) || null;

    setManualAllotmentData(manualSpec);
    setSelectedProcessType(manualProc || null);
    setSelectedTransaction(txn || null);
    setShowShareChangesSubRow(true);
    onClick(row);
  };

  const hasAllocationsInTransactions = (officialId) => {
    const txs = normalizedTxns || [];
    for (const t of txs) {
      const changes = t?.shareCapitalChanges || [];
      for (const sc of changes) {
        const allocations = sc?.allocations || [];
        const match = allocations.find(
          (a) =>
            String(a?.officialId) === String(officialId) &&
            Number(a?.allocatedShares || 0) > 0
        );
        if (match) return true;
      }
    }
    return false;
  };

  const [resolvedAlerts, setResolvedAlerts] = useState(new Set());

  // Check for unresolved alerts
  useEffect(() => {
    if (!props.onUnresolvedAlertsChange) return;

    // User Request: Only users in Current Appointments table affect the validation button
    if (isChangesList) {
      props.onUnresolvedAlertsChange(false);
      return;
    }

    const hasUnresolved = (() => {
      if (!shareholderPercentageChanges) return false;

      const alertIds = Object.keys(shareholderPercentageChanges).filter(
        (id) => {
          const changeObj = shareholderPercentageChanges[id];
          const hasChanged =
            !!changeObj &&
            ((typeof changeObj.shareholdingChange !== "undefined" &&
              Math.abs(changeObj.shareholdingChange) > 0) ||
              (typeof changeObj.votingRightsChange !== "undefined" &&
                Math.abs(changeObj.votingRightsChange) > 0));

          // Also check if valid designation (similar to shouldShowIcon)
          const official = data.find((o) => o._id === id);
          const isValidDesignation = official
            ? isShareholderDesignation({ original: official })
            : false;

          return hasChanged && isValidDesignation;
        }
      );

      return alertIds.some((id) => !resolvedAlerts.has(id));
    })();

    props.onUnresolvedAlertsChange(hasUnresolved);
  }, [
    shareholderPercentageChanges,
    resolvedAlerts,
    props.onUnresolvedAlertsChange,
    data,
  ]);

  const handleAdjustBODistribution = (row) => {
    // Find an active transaction to attach this BO update to.
    // We prefer Manual Allotment, but Pro-Rata or Rights Issue should also work
    // if they are present in normalizedTxns.
    const manualProc = [
      "privateWithMoreThanOneClass",
      "PVTwithOneClass",
      "existingAuth",
      "unallotedShares",
    ].find((p) => {
      const t = findTxnByProcess(p);
      return (
        t &&
        Array.isArray(t.shareCapitalChanges) &&
        t.shareCapitalChanges.some(
          (sc) =>
            sc?.changeType === p &&
            String(sc?.option || "").toLowerCase() === "manual"
        )
      );
    });

    let txn = manualProc ? findTxnByProcess(manualProc) : null;
    let procType = manualProc;

    // If no specific manual process found, look for ANY transaction that has shareCapitalChanges
    if (!txn) {
      txn = normalizedTxns.find(
        (t) =>
          t.transactionType === "MANUAL" &&
          Array.isArray(t.shareCapitalChanges) &&
          t.shareCapitalChanges.length > 0
      );
      if (txn) {
        procType = txn.processType;
      }
    }

    if (txn) {
      setSelectedProcessType(procType || null);
      setSelectedTransaction(txn || null);
    }

    const officialId = row?.original?._id;
    setBoOnlyViewOfficialId(officialId);
    setInitialSelectedTabByOfficial((prev) => ({
      ...prev,
      [officialId]: "officialShareAllocation",
    }));
    setShowShareChangesSubRow(true);
    if (!row.isExpanded) {
      row.toggleAllRowsExpanded(false);
      row.toggleRowExpanded();
    } else {
      // If already expanded but not showing share changes, we need to ensure it shows
      // But if it is expanded, the subrow is already visible.
      // If we just switched to boOnlyView, the subrow needs to re-render with new props.
    }
  };

  const setInitiateTransactionObj = (row, reason, selectType) => {
    const initiateTransactionObj = {
      changeType: "MANUAL-CESSATION",
      applicationType: row?.original?.designation,
      reason,
      selectType,
    };
    window.localStorage.setItem(
      "initiateTransactionObj",
      JSON.stringify(initiateTransactionObj)
    );
    setCessationIsSelected(true);
    onClick(row);
  };

  const handleRemoval = (row) =>
    setInitiateTransactionObj(row, "removal", "removal");
  const handleResignation = (row) =>
    setInitiateTransactionObj(row, "resignation", "resignation");
  const handleDeath = (row) =>
    setInitiateTransactionObj(row, "death", "lackOfCapacity");
  const handleBankruptcy = (row) =>
    setInitiateTransactionObj(row, "bankruptcy", "lackOfCapacity");
  const handleMentalIncapacity = (row) =>
    setInitiateTransactionObj(row, "insanity", "lackOfCapacity");
  const handleCourtOrder = (row) =>
    setInitiateTransactionObj(row, "courtOrder", "lackOfCapacity");
  const handleRetirement = (row) =>
    setInitiateTransactionObj(row, "retirement", "operation");
  const handleRotation = (row) =>
    setInitiateTransactionObj(row, "rotation", "operation");

  const handleShareChangesSubRow = (row) => {
    setShowShareChangesSubRow(true);
    onClick(row);
  };

  const handleChangesListDelete = async (original) => {
    let transaction = null;
    if (transactions?.length > 0) {
      transaction = transactions.find(
        (trx) => trx._id === original.transaction
      );
      if (transaction) {
        const { officials, companyId, beneficialOwners, shareCapitalChanges } =
          transaction;

        const isManual =
          transaction.transactionType === Constants.TRANSACTION_TYPES.MANUAL;

        if (isManual) {
          // 1. Clean up official status for the specific user
          if (
            transaction?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.APPOINTMENT
          ) {
            const official = officials?.find(
              (o) => String(o._id) === String(original._id)
            );
            if (official) {
              if (official?.tempDesignation) {
                await dispatch(
                  updateCompanyOfficialAsync({
                    officialId: official._id,
                    data: { tempDesignation: "" },
                  })
                );
              } else {
                await dispatch(
                  deleteCompanyOfficialAsync({
                    officialId: official._id,
                    idType: official.idType,
                    companyId,
                  })
                );
              }
            }
          }

          if (
            transaction?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS
          ) {
            const official = officials?.find(
              (o) => String(o._id) === String(original._id)
            );
            if (official) {
              await dispatch(
                updateCompanyOfficialAsync({
                  officialId: official._id,
                  data: {
                    companyId,
                    idType: official.idType,
                    changedFields: {},
                  },
                })
              );
            }

            if (beneficialOwners) {
              const userBos = beneficialOwners.filter(
                (bo) => String(bo.officialId) === String(original._id)
              );
              userBos.forEach(async (bo) => {
                if (bo?._id) {
                  await dispatch(
                    editBeneficialOwnerAsync({
                      companyId,
                      beneficialOwnerId: bo?._id,
                      data: { changedFields: {} },
                    })
                  );
                }
              });
            }
          }

          // 2. Update transaction data
          let updatedShareCapitalChanges = shareCapitalChanges
            ? [...shareCapitalChanges]
            : [];
          let updatedBeneficialOwners = beneficialOwners
            ? [...beneficialOwners]
            : [];
          let updatedOfficials = officials ? [...officials] : [];
          let hasChanges = false;

          // Remove from allocations
          if (updatedShareCapitalChanges.length > 0) {
            updatedShareCapitalChanges = updatedShareCapitalChanges.map(
              (sc) => {
                if (sc.allocations && sc.allocations.length > 0) {
                  const newAllocations = sc.allocations.filter(
                    (a) => String(a.officialId) !== String(original._id)
                  );
                  if (newAllocations.length !== sc.allocations.length) {
                    hasChanges = true;
                    return { ...sc, allocations: newAllocations };
                  }
                }
                return sc;
              }
            );
          }

          // Remove from BOs
          if (updatedBeneficialOwners.length > 0) {
            const initialLength = updatedBeneficialOwners.length;
            updatedBeneficialOwners = updatedBeneficialOwners.filter(
              (bo) => String(bo.officialId) !== String(original._id)
            );
            if (updatedBeneficialOwners.length !== initialLength) {
              hasChanges = true;
            }
          }

          // Remove from Officials
          if (updatedOfficials.length > 0) {
            const initialLength = updatedOfficials.length;
            updatedOfficials = updatedOfficials.filter(
              (o) => String(o._id) !== String(original._id)
            );
            if (updatedOfficials.length !== initialLength) {
              hasChanges = true;
            }
          }

          const hasAllocations = updatedShareCapitalChanges.some(
            (sc) => sc.allocations && sc.allocations.length > 0
          );
          const hasBeneficialOwners = updatedBeneficialOwners.length > 0;
          const hasOfficials = updatedOfficials.length > 0;

          if (!hasAllocations && !hasBeneficialOwners && !hasOfficials) {
            await dispatch(
              deleteInitiatedTransactionChangeAsync({
                companyId,
                initiateId: transaction._id,
              })
            );
            toast("success", "Application has been deleted successfully");
          } else if (hasChanges) {
            await handleCompanyMaintenanceFormData(
              "existing-request",
              {
                companyId,
                id: transaction._id,
                formName: "initiateTransactionChange",
                shareCapitalChanges: updatedShareCapitalChanges,
                beneficialOwners: updatedBeneficialOwners,
                officials: updatedOfficials,
              },
              companyId
            );
            toast("success", "Official removed from application successfully");
          }
        } else {
          // Standard transaction - delete entire transaction
          if (
            transaction?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.APPOINTMENT
          ) {
            if (officials?.length > 0) {
              officials.forEach(async (official) => {
                if (official?.tempDesignation) {
                  await dispatch(
                    updateCompanyOfficialAsync({
                      officialId: official._id,
                      data: {
                        tempDesignation: "",
                      },
                    })
                  );
                } else {
                  await dispatch(
                    deleteCompanyOfficialAsync({
                      officialId: official._id,
                      idType: official.idType,
                      companyId,
                    })
                  );
                }
              });
            }
          }
          if (
            transaction?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS
          ) {
            if (officials?.length > 0) {
              officials.forEach(async (official) => {
                if (official?._id) {
                  await dispatch(
                    updateCompanyOfficialAsync({
                      officialId: official._id,
                      data: {
                        companyId,
                        idType: official.idType,
                        changedFields: {},
                      },
                    })
                  );
                }
              });
            }

            if (beneficialOwners && beneficialOwners?.length > 0) {
              beneficialOwners.forEach(async (bo) => {
                if (bo?._id) {
                  await dispatch(
                    editBeneficialOwnerAsync({
                      companyId,
                      beneficialOwnerId: bo?._id,
                      data: {
                        changedFields: {},
                      },
                    })
                  );
                }
              });
            }
          }
          await dispatch(
            deleteInitiatedTransactionChangeAsync({
              companyId,
              initiateId: transaction._id,
            })
          );
          setResolvedAlerts((prev) => {
            const next = new Set(prev);
            next.delete(original._id);
            return next;
          });
          toast("success", "Application has been deleted successfully");
        }
      }
    }
    const filteredIds = createdTransactionsIds.filter(
      (id) => id !== original?.transaction
    );
    setCreatedTransactionsIds(filteredIds);
    dispatch(getCompanyOfficialsAsync({ companyId, queryParams: {} }));
    if (filteredIds.length === 0) {
      dispatch(resetTransactionChange());
    } else {
      setCreatedTransactionsIds(filteredIds);
    }
  };

  const isShareholderDesignation = (row) =>
    [
      "Shareholder",
      "Joint Shareholder",
      "Director",
      "Director Shareholder",
    ].includes(row?.original?.designation) ||
    (Array.isArray(row?.original?.sharesAllotted) &&
      row?.original?.sharesAllotted.length > 0);

  // Check if manual allotment is available for shareholder
  const hasManualAllotmentAvailable = (row) => {
    return (
      isShareholderDesignation(row) &&
      manualAllotAvailable &&
      hasPendingManualAllocations
    );
  };

  // If there is an allotment process but manual option exists, hide the standard Allot
  const computeShowAllot = (allowForRow) => {
    const allotProcessPresent =
      flagsByProcess.privateWithMoreThanOneClass ||
      flagsByProcess.PVTwithOneClass ||
      flagsByProcess.existingAuth ||
      flagsByProcess.unallotedShares;
    const showAllotStandard = allotProcessPresent && !manualAllotAvailable;
    return allowForRow && showAllotStandard;
  };

  // Calculate remainder amounts for changes list
  const remainderAmounts = useMemo(() => {
    if (!isChangesList) return null;
    const remaindersByClass = new Map();

    // Use Redux state data for real-time updates, fallback to props.data
    const dataSource =
      initiatedTransactionChanges?.length > 0
        ? initiatedTransactionChanges
        : props.data;

    // Extract remainder data from shareCapitalChanges array in each item
    (dataSource || []).forEach((item, index) => {
      // Check if item has shareCapitalChanges array
      if (
        item?.shareCapitalChanges &&
        Array.isArray(item.shareCapitalChanges)
      ) {
        item.shareCapitalChanges.forEach((shareChange, changeIndex) => {
          const classOfShares = shareChange?.classOfShares;
          const remainder = shareChange?.remainder;

          // Only process if both classOfShares and remainder exist
          if (
            classOfShares &&
            typeof remainder !== "undefined" &&
            remainder !== null
          ) {
            const numRemainder = Number(remainder) || 0;

            // Only add to map if remainder > 0 (has pending allocation)
            if (numRemainder > 0) {
              if (remaindersByClass.has(classOfShares)) {
                remaindersByClass.set(
                  classOfShares,
                  remaindersByClass.get(classOfShares) + numRemainder
                );
              } else {
                remaindersByClass.set(classOfShares, numRemainder);
              }
            }
          }
        });
      }

      // Also check if the item itself has remainder and classOfShares (fallback)
      if (item?.remainder && item?.classOfShares) {
        const numRemainder = Number(item.remainder) || 0;
        if (numRemainder > 0) {
          const classOfShares = item.classOfShares;
          if (remaindersByClass.has(classOfShares)) {
            remaindersByClass.set(
              classOfShares,
              remaindersByClass.get(classOfShares) + numRemainder
            );
          } else {
            remaindersByClass.set(classOfShares, numRemainder);
          }
        }
      }
    });

    // Convert to array of objects for easier rendering - only classes with remainder > 0
    const result = Array.from(remaindersByClass.entries())
      .filter(([, remainder]) => remainder > 0)
      .map(([classOfShares, remainder]) => ({
        classOfShares,
        remainder,
      }));

    return result;
  }, [isChangesList, props.data, initiatedTransactionChanges]);

  const columns = useMemo(
    () =>
      [
        // Warning Icon Column - Always first
        {
          Header: "",
          id: "warningIcon",
          width: 60,
          Cell: ({ row: { original } }) => {
            const changeObj =
              shareholderPercentageChanges &&
              shareholderPercentageChanges[String(original._id)];
            const isDiluted =
              !!changeObj &&
              (typeof changeObj.hasDecreased !== "undefined"
                ? changeObj.hasDecreased
                : Number(changeObj.change) < 0);
            const isValidDesignation = isShareholderDesignation({ original });

            // Show warning icon on both Current Appointments and Changes List
            // User Request: Only show on Current Appointments table (not Changes List)
            const shouldShowIcon =
              isDiluted && isValidDesignation && !isChangesList;

            return (
              <div className="flex items-center justify-center">
                {shouldShowIcon ? (
                  <ShareholderPercentageWarningIcon
                    change={
                      shareholderPercentageChanges
                        ? shareholderPercentageChanges[String(original._id)]
                        : null
                    }
                    className="group"
                  />
                ) : null}
              </div>
            );
          },
        },
        assigningRegistrar && {
          Header: "",
          id: "registrarSelection",
          Cell: ({ row }) => {
            if (registrarCandidatesIds.includes(row.original._id)) {
              return (
                <Checkbox
                  name={row.original._id}
                  checked={selectedRegistrar === row.original._id}
                  disabled={
                    selectedRegistrar !== row.original._id && selectedRegistrar
                  }
                  onChange={handleToggleRegistrar}
                />
              );
            }
            return <span>-</span>;
          },
        },
        {
          Header: "Full Name",
          accessor: "fullName",
          className: "p-4 text-ellipsis overflow-hidden",
          Cell: ({ row: { original } }) => {
            const email =
              original.type === "invited"
                ? getMaskedEmail(
                    original.emailAddress ||
                      original.electronicAddress?.emailAddress ||
                      original.guardianEmailAddress ||
                      "-"
                  )
                : original.emailAddress ||
                  original.electronicAddress?.emailAddress ||
                  original?.electronicAddress?.emailAddress ||
                  "-";
            let nameContent = original.fullName;
            if (Array.isArray(nameContent)) {
              nameContent = nameContent.join(", ");
            }
            if (!nameContent) {
              nameContent =
                original?.names?.companyName ||
                getFullName(original, false) ||
                getFullName(original?.names, false) ||
                "-";
            }

            // Check if this is a share transfer transaction
            let transferRole = null;

            // Check if we're in changes list or update company details context with transactions
            if (
              (isChangesList || isInUpdateCompanyDetailsContext) &&
              transactions &&
              Array.isArray(transactions)
            ) {
              // Find share transfer transactions
              const shareTransferTx = transactions.find(
                (tx) =>
                  tx.processType === "shareTransfer" &&
                  tx.shareCapitalChanges &&
                  Array.isArray(tx.shareCapitalChanges)
              );

              if (shareTransferTx) {
                // Check allocations to determine role
                shareTransferTx.shareCapitalChanges.forEach((change) => {
                  if (change.allocations && Array.isArray(change.allocations)) {
                    const allocation = change.allocations.find(
                      (alloc) =>
                        String(alloc.officialId) === String(original._id)
                    );
                    if (allocation) {
                      if (allocation.isTransferor === true) {
                        transferRole = "transferor";
                      } else if (allocation.isTransferor === false) {
                        transferRole = "transferee";
                      }
                    }
                  }
                });
              }
            }

            return (
              <div>
                <div className="flex items-center space-x-1">
                  <div className="flex-shrink-0">
                    {!original?.passportPhoto &&
                    !original?.fileUploads?.passportPhoto ? (
                      <div className="w-8 h-8 rounded-full">
                        <div className="relative w-8 h-8 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
                          <AvatarIcon />
                        </div>
                      </div>
                    ) : original?.fileUploads?.passportPhoto ? (
                      <img
                        className="w-8 h-8 rounded-full"
                        src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${original?.fileUploads?.passportPhoto}`}
                        alt={original.fullName || "Official"}
                      />
                    ) : (
                      <img
                        className="w-8 h-8 rounded-full"
                        src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${original.passportPhoto}`}
                        alt={original.fullName || "Official"}
                      />
                    )}
                  </div>
                  <div className="px-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="font-bold">{nameContent}</h1>
                    </div>
                    <div className="flex items-center gap-6">
                      <p className="m-0">{email}</p>
                      {transferRole && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                            transferRole === "transferor"
                              ? "bg-red-50 text-red-700 border border-red-200"
                              : "bg-green-50 text-green-700 border border-green-200"
                          }`}
                        >
                          {transferRole === "transferor"
                            ? "Transferor →"
                            : "← Transferee"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  {original.designation === "Alternate Director" ? (
                    <p className="text-xs">
                      Alternate Director to{" "}
                      {original.appointers
                        .map((item) => getFullName(item?.names) || "N/A")
                        .join(", ")}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          },
        },
        {
          Header: "ID Type",
          accessor: "idType",
          Cell: ({ value }) => <span>{value || "-"}</span>,
        },
        {
          Header: "Designation",
          accessor: "designation",
          Cell: ({ row: { original } }) => {
            const designation = Array.isArray(original.fullName)
              ? "Joint Shareholder"
              : original.isDirector === "Yes"
              ? "Director"
              : original.designation || "-";
            return <span>{designation}</span>;
          },
        },
        {
          Header: "Shares Allotted",
          accessor: "allocatedShares",
          Cell: ({ row: { original } }) => {
            if (isChangesList) {
              // Check if user has explicit allocations in pending transactions
              let hasPendingAllocation = false;
              const checkAllocations = (txns) => {
                if (!txns || !Array.isArray(txns)) return false;
                return txns.some((txn) =>
                  txn.shareCapitalChanges?.some((sc) =>
                    sc.allocations?.some(
                      (a) =>
                        String(a.officialId) === String(original._id) &&
                        Number(a.allocatedShares) > 0
                    )
                  )
                );
              };

              if (
                checkAllocations(transactions) ||
                checkAllocations(initiatedTransactionChanges)
              ) {
                hasPendingAllocation = true;
              }

              if (!hasPendingAllocation) {
                return <span>-</span>;
              }

              let shareCapitalChanges = [];

              if (transactions && Array.isArray(transactions)) {
                transactions.forEach((transaction) => {
                  if (
                    transaction.shareCapitalChanges &&
                    Array.isArray(transaction.shareCapitalChanges)
                  ) {
                    shareCapitalChanges = shareCapitalChanges.concat(
                      transaction.shareCapitalChanges
                    );
                  }
                });
              }

              // 2. From initiatedTransactionChanges (real-time updates)
              if (
                initiatedTransactionChanges &&
                Array.isArray(initiatedTransactionChanges)
              ) {
                initiatedTransactionChanges.forEach((transaction) => {
                  if (
                    transaction.shareCapitalChanges &&
                    Array.isArray(transaction.shareCapitalChanges)
                  ) {
                    shareCapitalChanges = shareCapitalChanges.concat(
                      transaction.shareCapitalChanges
                    );
                  }
                });
              }
              if (
                shareCapitalChanges.length === 0 &&
                original.shareCapitalChanges &&
                Array.isArray(original.shareCapitalChanges)
              ) {
                shareCapitalChanges = original.shareCapitalChanges;
              }

              if (shareCapitalChanges.length > 0 && original._id) {
                const allocation = getSharesFromTransactions(
                  original._id,
                  shareCapitalChanges
                );
                if (allocation !== "-") {
                  return <span>{allocation}</span>;
                }
              }

              // Fallback 1 & 2 removed for Changes List to prevent showing existing shares
              // for users who only have BO adjustments or other non-share changes.
              // We only want to show shares that are explicitly allocated in the pending transactions.

              // Fallback 3: Check if row itself is an allocation record
              if (
                original.officialId &&
                original.classOfShares &&
                original.allocatedShares
              ) {
                return (
                  <span>
                    {`${original.classOfShares}: ${original.allocatedShares}`}
                  </span>
                );
              }

              return <span>-</span>;
            }

            // For non-changes list mode
            if (
              original.isDirector === "Yes" &&
              !Array.isArray(original.fullName)
            ) {
              return <span>-</span>;
            }

            // Use sharesAllotted array for regular display too
            if (
              original.sharesAllotted &&
              Array.isArray(original.sharesAllotted) &&
              original.sharesAllotted.length > 0
            ) {
              const allocation = transformSharesAllotted(
                original.sharesAllotted
              );
              return <span>{allocation}</span>;
            }

            return <span>-</span>;
          },
        },
        {
          Header: "Status",
          accessor: "status",
          Cell: ({ value, row }) => {
            if (isChangesList) {
              return <span>{value || "Pending"}</span>;
            }
            if (row.original.type && row.original.type === "invited") {
              return <span>{row.original.inviteStatus || "-"}</span>;
            }
            return <span>{value || "-"}</span>;
          },
        },
        {
          Header: "Action",
          Cell: ({ row }) => {
            const isJointShareholderRow =
              row.original.designation === "Joint Shareholder";

            const unFlattenRow =
              originalCompanyOfficials.find(
                (official) => official._id === row.original._id
              ) || row.original;

            const isOwnerRow = [
              unFlattenRow?.electronicAddress?.emailAddress,
              unFlattenRow?.identificationDetails?.emailAddress,
              unFlattenRow?.identificationDetails?.guardianEmailAddress,
            ].includes(currentUser.electronicAddress?.emailAddress);

            const isApplicant =
              currentUser?._id === company?.createdBy?._id ||
              Constants.COOKIE_ROLES.CO_APPLICANT;

            const disableEditBtn =
              (row.original?.type === "invited" ||
                role === Constants.COOKIE_ROLES.OFFICIAL) &&
              !isOwnerRow;

            const officialComments =
              comments.filter(
                (comment) =>
                  comment?.officialId &&
                  comment.officialId === row.original._id &&
                  comment?.tabName &&
                  comment.tabName === currentTab
              )?.length ?? 0;

            const allowForRow = isShareholderDesignation(row);
            const showAuthAndAllot = computeShowAllot(allowForRow);

            // In UpdateCompanyDetails context
            if (isInUpdateCompanyDetailsContext) {
              if (row?.isExpanded) {
                return (
                  <TableActionMenu
                    row={row}
                    showCloseBtn={true}
                    hideEditBtn={true}
                    hideDeleteBtn={true}
                    handleClose={handleClose}
                  />
                );
              }

              if (isChangesList) {
                return (
                  <div className="relative flex items-center space-x-2">
                    <TableActionMenu
                      isChangesList={true}
                      deleteConfirmationLabel="Are you sure you want to permanently delete this entry? This will also delete any linked beneficial owners, if present."
                      row={{
                        ...row,
                        original: { ...row.original, isJointShareholderRow },
                      }}
                      showViewBtn={role !== Constants.COOKIE_ROLES.OFFICIAL}
                      handleView={handleView}
                      handleEdit={handleEdit}
                      handleDelete={handleChangesListDelete}
                      showAdjustBODistribution={false}
                      handleAdjustBODistribution={() =>
                        handleAdjustBODistribution(row)
                      }
                      // For changes list, only show View, Edit, Delete - hide all other actions
                      showManualAllot={false}
                      showAuthorizationAndAllotment={false}
                      showShareTransfer={false}
                      showTransmissionOfShares={false}
                      showForfeitureOfShares={false}
                      showSurrenderOfShares={false}
                      showSharesBuyBack={false}
                      showCancellationOfTreasuryShares={false}
                      showSaleTransferOfTreasuryShares={false}
                      showSharesRedemption={false}
                      showNoticeToCourt={false}
                      showTakeOverOffer={false}
                      showSharesConsolidation={false}
                      showSharesSubdivision={false}
                    />
                    {officialComments > 0 && (
                      <span className="absolute flex items-center justify-center min-w-[4] h-4 p-1 text-white bg-red-700 rounded-full -top-3 right-0">
                        {officialComments}
                      </span>
                    )}
                  </div>
                );
              }

              // Not changesList; still in UpdateCompanyDetailsContext
              return (
                <div className="relative flex items-center space-x-2">
                  <TableActionMenu
                    deleteConfirmationLabel="Are you sure you want to permanently delete this entry? This will also delete any linked beneficial owners, if present."
                    row={{
                      ...row,
                      original: { ...row.original, isJointShareholderRow },
                    }}
                    showViewBtn={role !== Constants.COOKIE_ROLES.OFFICIAL}
                    handleView={
                      isJointShareholderRow
                        ? handleEditAndDeleteJointShareholder
                        : handleView
                    }
                    // Transaction-driven actions - only show in UpdateCompanyDetails context
                    showManualAllot={
                      !mode?.viewing && hasManualAllotmentAvailable(row)
                    }
                    handleManualAllot={() => handleManualAllot(row)}
                    showAdjustBODistribution={(() => {
                      if (mode?.viewing) return false;
                      const id = row?.original?._id;
                      const changeObj =
                        shareholderPercentageChanges &&
                        id &&
                        shareholderPercentageChanges[String(id)];
                      const hasChanged =
                        !!changeObj &&
                        ((typeof changeObj.shareholdingChange !== "undefined" &&
                          Math.abs(changeObj.shareholdingChange) > 0) ||
                          (typeof changeObj.votingRightsChange !==
                            "undefined" &&
                            Math.abs(changeObj.votingRightsChange) > 0));
                      const isShareholder = isShareholderDesignation(row);
                      // const hasAlloc = hasAllocationsInTransactions(id);
                      return (
                        isShareholder && hasChanged && !isChangesList // User Request: Only show on Current Appointments table
                        // !hasAlloc && // Allow adjustment even if already allocated
                        // !hasAnyRemainderAcrossTxns // Allow adjustment even if manual remainder exists
                      );
                    })()}
                    handleAdjustBODistribution={() =>
                      handleAdjustBODistribution(row)
                    }
                    hideDeleteBtn={true}
                    hideEditBtn={true}
                    showChange={
                      !mode?.viewing &&
                      changeOfParticularsDesignations?.length > 0 &&
                      changeOfParticularsDesignations.includes(
                        row?.original?.designation
                      )
                    }
                    showAppointAsDirector={
                      !mode?.viewing &&
                      canAppointDirectorShareholder() &&
                      row.original.designation ===
                        Constants.APPLICANT_CAPACITIES.SHAREHOLDER
                    }
                    showCeaseAsDirector={
                      !mode?.viewing &&
                      canCeaseDirectorShareholder() &&
                      row.original.designation ===
                        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
                    }
                    handleCeaseAsDirector={handleCeaseAsDirector}
                    handleAppointAsDirector={handleAppointAsDirector}
                    handleChange={handleChange}
                    showRemoval={
                      !mode?.viewing &&
                      row?.original.designation !==
                        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER &&
                      cessationDesignations?.length > 0 &&
                      cessationDesignations.includes(row?.original?.designation)
                    }
                    handleRemoval={handleRemoval}
                    showResignation={
                      !mode?.viewing &&
                      row?.original.designation !==
                        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER &&
                      cessationDesignations?.length > 0 &&
                      cessationDesignations.includes(row?.original?.designation)
                    }
                    handleResignation={handleResignation}
                    showAuthorizationAndAllotment={
                      !mode?.viewing && showAuthAndAllot
                    }
                    handleAuthorizationAndAllotment={() =>
                      openShareChanges(row, "authorization")
                    }
                    showShareTransfer={(() => {
                      if (mode?.viewing) return false;
                      if (!allowForRow || !flagsByProcess.shareTransfer)
                        return false;

                      // Find share transfer transaction
                      const shareTransferTxn = normalizedTxns.find(
                        (t) =>
                          t?.transactionType === "MANUAL" &&
                          t?.processType === "shareTransfer"
                      );

                      if (!shareTransferTxn?.shareCapitalChanges) return false;

                      // Check if all remainders are zero
                      const allRemainderZero =
                        shareTransferTxn.shareCapitalChanges.every((sc) => {
                          if (
                            sc?.remainder === undefined ||
                            sc?.remainder === null
                          )
                            return false;
                          return Number(sc.remainder) === 0;
                        });

                      const designation = row?.original?.designation;
                      const isDirector = designation === "Director";

                      // Directors only see Share Transferee (when remainder is zero)
                      if (isDirector) {
                        return allRemainderZero;
                      }

                      // For shareholders: check if remainder is NOT zero (Show Share Transferor)
                      if (!allRemainderZero) {
                        // Check if shareholder has the class of shares being transferred
                        const officialShares =
                          row?.original?.sharesAllotted || [];
                        const hasMatchingClass =
                          shareTransferTxn.shareCapitalChanges.some((sc) => {
                            return officialShares.some(
                              (share) =>
                                share.classOfShares === sc.classOfShares
                            );
                          });
                        return hasMatchingClass;
                      }

                      // Show Share Transferee for shareholders when remainder is zero
                      return allRemainderZero;
                    })()}
                    shareTransferButtonLabel={(() => {
                      // Find share transfer transaction for this official
                      const shareTransferTxn = normalizedTxns.find(
                        (t) =>
                          t?.transactionType === "MANUAL" &&
                          t?.processType === "shareTransfer"
                      );

                      if (!shareTransferTxn?.shareCapitalChanges)
                        return "Share Transferor";

                      // Check if all share capital changes have remainder === 0
                      const allRemainderZero =
                        shareTransferTxn.shareCapitalChanges.every((sc) => {
                          // If remainder field doesn't exist, return false (treat as non-zero)
                          if (
                            sc?.remainder === undefined ||
                            sc?.remainder === null
                          )
                            return false;
                          return Number(sc.remainder) === 0;
                        });

                      // If all remainders are exactly zero, show "Share Transferee", otherwise "Share Transferor"
                      return allRemainderZero
                        ? "Share Transferee"
                        : "Share Transferor";
                    })()}
                    handleShareTransfer={() =>
                      openShareChanges(row, "shareTransfer")
                    }
                    showTransmissionOfShares={
                      allowForRow && flagsByProcess.transmissionOfShares
                    }
                    handleTransmissionOfShares={() =>
                      openShareChanges(row, "transmissionOfShares")
                    }
                    showForfeitureOfShares={
                      allowForRow && flagsByProcess.forfeitureOfShares
                    }
                    handleForfeitureOfShares={() =>
                      openShareChanges(row, "forfeitureOfShares")
                    }
                    showSurrenderOfShares={
                      allowForRow && flagsByProcess.surrenderOfShares
                    }
                    handleSurrenderOfShares={() =>
                      openShareChanges(row, "surrenderOfShares")
                    }
                    showSharesBuyBack={
                      allowForRow && flagsByProcess.sharesBuyback
                    }
                    handleSharesBuyBack={() =>
                      openShareChanges(row, "sharesBuyback")
                    }
                    showCancellationOfTreasuryShares={
                      allowForRow && flagsByProcess.cancelationOfTreasuryShares
                    }
                    handleCancellationOfTreasuryShares={() =>
                      openShareChanges(row, "cancelationOfTreasuryShares")
                    }
                    showSaleTransferOfTreasuryShares={
                      allowForRow && flagsByProcess.saleTransferOfTreasuryShares
                    }
                    handleSaleTransferOfTreasuryShares={() =>
                      openShareChanges(row, "saleTransferOfTreasuryShares")
                    }
                    showSharesRedemption={
                      allowForRow && flagsByProcess.sharesRedemption
                    }
                    handleSharesRedemption={() =>
                      openShareChanges(row, "sharesRedemption")
                    }
                    showNoticeToCourt={
                      allowForRow && flagsByProcess.noticeToCourt
                    }
                    handleNoticeToCourt={() =>
                      openShareChanges(row, "noticeToCourt")
                    }
                    showTakeOverOffer={
                      allowForRow && flagsByProcess.takeoverOffer
                    }
                    handleTakeOverOffer={() =>
                      openShareChanges(row, "takeoverOffer")
                    }
                    showSharesConsolidation={
                      allowForRow && flagsByProcess.sharesConsolidation
                    }
                    handleSharesConsolidation={() =>
                      openShareChanges(row, "sharesConsolidation")
                    }
                    showSharesSubdivision={
                      allowForRow && flagsByProcess.sharesSubdivision
                    }
                    handleSharesSubdivision={() =>
                      openShareChanges(row, "sharesSubdivision")
                    }
                  />
                  {officialComments > 0 && (
                    <span className="absolute flex items-center justify-center min-w-[4] h-4 p-1 text-white bg-red-700 rounded-full -top-3 right-0">
                      {officialComments}
                    </span>
                  )}
                </div>
              );
            }

            // -------- Default (outside UpdateCompanyDetailsContext) --------
            return (
              <div className="relative flex items-center space-x-2">
                <button
                  type="button"
                  id={`${row.original?.JSGroupID ?? row?.original?._id}_view`}
                  onClick={() =>
                    isJointShareholderRow
                      ? handleEditAndDeleteJointShareholder({
                          ...row.original,
                          isViewOnly: true,
                        })
                      : handleEdit({ ...row.original, isViewOnly: true })
                  }
                  hidden
                >
                  View
                </button>
                <button
                  type="button"
                  id={row.original?.JSGroupID ?? row?.original?._id}
                  onClick={() =>
                    isJointShareholderRow
                      ? handleEditAndDeleteJointShareholder(row.original)
                      : handleEdit(row.original)
                  }
                  hidden
                >
                  Edit
                </button>
                <button
                  type="button"
                  id={`${
                    row.original?.JSGroupID ?? row?.original?._id
                  }_reviewButton`}
                  onClick={() => handleReview(row)}
                  hidden
                >
                  Review Button
                </button>

                <TableActionMenu
                  deleteConfirmationLabel="Are you sure you want to permanently delete this entry? This will also delete any linked beneficial owners, if present."
                  row={{
                    ...row,
                    original: { ...row.original, isJointShareholderRow },
                  }}
                  showRecallBtn={
                    row.original?.type === "invited" &&
                    row.original?.inviteStatus === "Awaiting Data" &&
                    unFlattenRow.createdBy === currentUser._id &&
                    !isJointShareholderRow
                  }
                  showResendInviteBtn={
                    row.original?.type === "invited" &&
                    row.original?.inviteStatus === "Awaiting Data" &&
                    unFlattenRow.createdBy === currentUser._id
                  }
                  showReviewBtn={
                    (![
                      unFlattenRow?.electronicAddress?.emailAddress,
                      unFlattenRow?.identificationDetails?.emailAddress,
                      unFlattenRow?.identificationDetails?.guardianEmailAddress,
                    ].includes(currentUser.electronicAddress?.emailAddress) &&
                      row.original?.type === "invited" &&
                      unFlattenRow.createdBy === currentUser._id &&
                      !isJointShareholderRow) ||
                    currentUser?._id === company?.createdBy?._id ||
                    Constants.COOKIE_ROLES.CO_APPLICANT
                  }
                  showViewBtn={role !== Constants.COOKIE_ROLES.OFFICIAL}
                  handleView={
                    isJointShareholderRow
                      ? handleEditAndDeleteJointShareholder
                      : handleView
                  }
                  handleRecall={handleRecallAccess}
                  handleEdit={
                    isJointShareholderRow
                      ? handleEditAndDeleteJointShareholder
                      : (r) =>
                          handleEdit({
                            ...r,
                            isViewOnly:
                              (row.original?.type === "invited" ||
                                role === Constants.COOKIE_ROLES.OFFICIAL) &&
                              ![
                                unFlattenRow?.electronicAddress?.emailAddress,
                                unFlattenRow?.identificationDetails
                                  ?.emailAddress,
                                unFlattenRow?.identificationDetails
                                  ?.guardianEmailAddress,
                              ].includes(
                                currentUser.electronicAddress?.emailAddress
                              ),
                          })
                  }
                  disableEditBtn={
                    (row.original?.type === "invited" ||
                      role === Constants.COOKIE_ROLES.OFFICIAL) &&
                    ![
                      unFlattenRow?.electronicAddress?.emailAddress,
                      unFlattenRow?.identificationDetails?.emailAddress,
                      unFlattenRow?.identificationDetails?.guardianEmailAddress,
                    ].includes(currentUser.electronicAddress?.emailAddress) &&
                    !(
                      currentUser?._id === company?.createdBy?._id ||
                      Constants.COOKIE_ROLES.CO_APPLICANT
                    )
                  }
                  disableDeleteBtn={
                    (row.original?.type === "invited" ||
                      role === Constants.COOKIE_ROLES.OFFICIAL) &&
                    ![
                      unFlattenRow?.electronicAddress?.emailAddress,
                      unFlattenRow?.identificationDetails?.emailAddress,
                      unFlattenRow?.identificationDetails?.guardianEmailAddress,
                    ].includes(currentUser.electronicAddress?.emailAddress) &&
                    !(
                      currentUser?._id === company?.createdBy?._id ||
                      Constants.COOKIE_ROLES.CO_APPLicant
                    )
                  }
                  handleDelete={(r) => {
                    r.isJointShareholderRow
                      ? handleEditAndDeleteJointShareholder(r)
                      : handleDelete(r);
                  }}
                  // Hide share-related actions outside UpdateCompanyDetails context
                  showManualAllot={false}
                  showAllotment={false}
                  showShareTransfer={false}
                  showTransmissionOfShares={false}
                  showForfeitureOfShares={false}
                  showSurrenderOfShares={false}
                  showSharesBuyBack={false}
                  showCancellationOfTreasuryShares={false}
                  showSaleTransferOfTreasuryShares={false}
                  showSharesRedemption={false}
                  showNoticeToCourt={false}
                  showTakeOverOffer={false}
                  showSharesConsolidation={false}
                  showSharesSubdivision={false}
                />
                {officialComments > 0 && (
                  <span className="absolute flex items-center justify-center min-w-[4] h-4 p-1 text-white bg-red-700 rounded-full -top-3 right-0">
                    {officialComments}
                  </span>
                )}
              </div>
            );
          },
        },
      ].filter(Boolean),
    [
      isChangesList,
      registrarCandidatesIds,
      selectedRegistrar,
      flagsByProcess,
      manualAllotAvailable,
      isInUpdateCompanyDetailsContext,
      shareholderPercentageChanges,
      resolvedAlerts,
    ]
  );

  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => {
      // Only show share changes subrow in UpdateCompanyDetails context
      if (showShareChangesSubRow && isInUpdateCompanyDetailsContext) {
        return (
          <ShareChangesSubRow
            row={row}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            transaction={selectedTransaction}
            initiatedProcessType={selectedProcessType}
            initiatedChangeType={
              selectedProcessType
                ? processTypeToChangeType[selectedProcessType]
                : null
            }
            shareCapitalChange={isChangesList ? row.original : null}
            manualAllotmentData={manualAllotmentData}
            onPercentageChanges={handlePercentageChangesUpdate}
            currentAppointmentsData={props.data} // Current table data
            changesListData={transactions} // Changes list data
            boOnlyView={boOnlyViewOfficialId === row.original._id}
            mode={isChangesList && isViewOnly ? { viewing: true } : props.mode}
            onBOUpdateSuccess={() =>
              setResolvedAlerts((prev) => new Set(prev).add(row.original._id))
            }
          />
        );
      }
      if (
        cessationIsSelected ||
        (row?.original?.cessationDate && editModeIsSelected)
      ) {
        return (
          <CessationSubRowForDelete
            row={row}
            visibleColumns={visibleColumns}
            changeList={[]}
          />
        );
      }
      if (
        changeOfParticularsIsSelected ||
        (row?.original?.changeOfParticularDate && editModeIsSelected) ||
        row?.original?.designation ===
          Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER
      ) {
        if (jointShareholdersToChange.length > 0) {
          return (
            <JointShareholderTableSubrow
              visibleColumns={visibleColumns}
              jointShareholders={jointShareholdersToChange}
            />
          );
        } else {
          const boOfficial = beneficialOwners.find(
            (bo) =>
              bo?.names?.firstName === row.original?.names?.firstName &&
              bo?.names?.lastName === row.original?.names?.lastName &&
              bo?.idType !== Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER
          );
          return (
            <ChangeOfParticularsSubRow
              row={row}
              visibleColumns={visibleColumns}
              changeList={[]}
              setChangeList={() => null}
              actionType=""
              boOfficial={boOfficial}
            />
          );
        }
      }

      return (
        <SubRowAsync
          row={{
            ...row,
            original: isChangesList
              ? row.original
              : originalCompanyOfficials.find(
                  (official) => official._id === row.original._id
                ) || row.original,
          }}
          originalCompanyOfficials={originalCompanyOfficials}
          rowProps={rowProps}
          isChangesList={isChangesList}
          data={props.data}
          visibleColumns={visibleColumns}
          currentTab={currentTab}
          remainderAmounts={remainderAmounts}
          transactions={initiatedTransactionChanges}
          shareholderPercentageChanges={shareholderPercentageChanges}
          onPercentageChangesUpdate={handlePercentageChangesUpdate}
          boOnlyView={boOnlyViewOfficialId === row.original._id}
          initialSelectedTabId={initialSelectedTabByOfficial[row.original._id]}
          editModeIsSelected={editModeIsSelected}
          mode={isChangesList && isViewOnly ? { viewing: true } : props.mode}
        />
      );
    },
    [
      isChangesList,
      originalCompanyOfficials,
      showShareChangesSubRow,
      cessationIsSelected,
      changeOfParticularsIsSelected,
      editModeIsSelected,
      selectedProcessType,
      selectedTransaction,
      manualAllotmentData,
      isInUpdateCompanyDetailsContext,
      jointShareholdersToChange,
      remainderAmounts,
      initiatedTransactionChanges,
      shareholderPercentageChanges,
      boOnlyViewOfficialId,
      initialSelectedTabByOfficial,
      props.mode,
      isViewOnly,
    ]
  );

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  const filteredData = props.data.filter((row) => {
    if (Array.isArray(row.fullName)) {
      return row.fullName.some((name) =>
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      const name = row.fullName || "";
      return name.toLowerCase().includes(searchTerm.toLowerCase());
    }
  });

  const startIndex = (currentPage - 1) * rowsPerPage;
  const totalRows = filteredData.length;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);

  return (
    <div className={`${isChangesList ? "mt-4" : ""}`}>
      <SectionDescription
        id=""
        title={`${
          isInManualTransactionReviewContext
            ? ""
            : isChangesList
            ? "Changes List"
            : "Current Appointments"
        }`}
        description=""
      />
      {!isChangesList && (
        <NotificationBanner notificationKey="flashMessageCOTable" />
      )}
      {isChangesList && remainderAmounts !== null && (
        <div className="flex justify-end my2">
          <div className="flex flex-col">
            {remainderAmounts.map((item, index) => (
              <div
                key={`${item.classOfShares}-${index}`}
                className="px-3 py-1 text-xs font-semibold bg-yellow-50 border border-gray-200 rounded"
              >
                <span className="text-gray-700">{item.classOfShares}:</span>
                <span className="ml-1 text-xs text-sflPrimary">
                  {item.remainder} Pending Allocation
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!isChangesList && (
        <div className="flex items-center justify-between my-2">
          <div className="flex items-center space-x-4">
            <h2 className="font-semibold">Filter by:</h2>
          </div>
          <SearchInput value={searchTerm} handleOnChange={handleSearch} />
        </div>
      )}

      <div className="w-full bg-white shadow">
        <div className="w-full">
          <ReactTable
            columns={columns}
            data={
              totalRows > rowsPerPage
                ? filteredData.slice(startIndex, endIndex)
                : filteredData
            }
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalRows={totalRows}
            setCurrentPage={setCurrentPage}
            paginate
            dataRowCellStyle="whitespace-normal"
            renderRowSubComponent={renderRowSubComponent}
          />
        </div>
      </div>
    </div>
  );
};

export default CompanyOfficialsTable;
